import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { API } from './constants';

export default function useValidationData() {
  const { getAuthHeader } = useAuth();
  
  // États principaux
  const [allDocuments, setAllDocuments] = useState([]);
  const [_recusESS, setRecusESS] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // États pour la sélection en masse
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  
  // Listes pour assignation
  const [coopers, setCoopers] = useState([]);
  const [members, setMembers] = useState([]);
  const [experts, setExperts] = useState([]);
  
  // États des filtres
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterMontantMin, setFilterMontantMin] = useState('');
  const [filterMontantMax, setFilterMontantMax] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // États du modal de validation
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [validationData, setValidationData] = useState({
    date_virement: new Date().toISOString().split('T')[0],
    montant_ttc: '',
    reference_virement: '',
    observations: ''
  });
  const [validating, setValidating] = useState(false);
  
  // États du modal de paiement partiel
  const [showPartialPaymentModal, setShowPartialPaymentModal] = useState(false);
  const [partialPaymentData, setPartialPaymentData] = useState({
    montant: '',
    date_paiement: new Date().toISOString().split('T')[0],
    mode_paiement: 'virement',
    reference: '',
    observations: '',
    type_paiement: 'partiel', // 'partiel' ou 'acompte'
    is_scheduled: false,
    scheduled_date: ''
  });
  const [partialPaymentsHistory, setPartialPaymentsHistory] = useState({
    paiements: [],
    montant_total_ttc: 0,
    total_paye: 0,
    reste_a_payer: 0
  });
  const [loadingPartialPayments, setLoadingPartialPayments] = useState(false);
  const [processingPartialPayment, setProcessingPartialPayment] = useState(false);
  
  // États du modal d'assignation
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignForm, setAssignForm] = useState({
    assign_type: '',
    assign_id: ''
  });
  
  // États du modal de partage
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareForm, setShareForm] = useState({
    recipients: [],
    method: 'email',
    message: '',
    include_recu: true
  });
  const [sharing, setSharing] = useState(false);
  
  // États du modal de modification d'échéance
  const [showEcheanceModal, setShowEcheanceModal] = useState(false);
  const [echeanceForm, setEcheanceForm] = useState({
    date_prochaine_echeance: '',
    observations: ''
  });
  const [updatingEcheance, setUpdatingEcheance] = useState(false);
  
  // États du modal d'échéancier personnalisé
  const [showEcheancierModal, setShowEcheancierModal] = useState(false);
  const [echeancierForm, setEcheancierForm] = useState({
    type_echeancier: 'manuel',
    echeances: [{ date: '', montant: '', observations: '' }]
  });
  const [creatingEcheancier, setCreatingEcheancier] = useState(false);
  
  // États pour l'archivage
  const [archiving, setArchiving] = useState(false);

  // États pour les filtres avancés de reçus
  const [filterRecuStatus, setFilterRecuStatus] = useState('all'); // all, recu_echeance, recu_acquitte, en_attente_recu
  const [filterDateDebut, setFilterDateDebut] = useState('');
  const [filterDateFin, setFilterDateFin] = useState('');
  
  // États pour le modal d'édition de reçu d'échéancier
  const [_showRecuEcheanceModal, setShowRecuEcheanceModal] = useState(false);
  const [_selectedPaiement, setSelectedPaiement] = useState(null);
  const [selectedPaiementIndex, setSelectedPaiementIndex] = useState(null);
  const [generatingRecu, setGeneratingRecu] = useState(false);

  // États pour la gestion Stripe SEPA
  const [showStripeSepaModal, setShowStripeSepaModal] = useState(false);
  const [stripeSepaType, setStripeSepaType] = useState('core'); // 'core' ou 'b2b'
  const [stripeProcessing, setStripeProcessing] = useState(false);
  const [_showStripeCBModal, setShowStripeCBModal] = useState(false);
  const [sepaModalEmail, setSepaModalEmail] = useState('');
  const [sepaModalName, setSepaModalName] = useState('');

  // États pour la gestion des acomptes ESS
  const [_showAcomptesTab, _setShowAcomptesTab] = useState(false);
  const [_acomptesData, _setAcomptesData] = useState([]);
  const [showEditAcompteModal, setShowEditAcompteModal] = useState(false);
  const [_selectedAcompte, setSelectedAcompte] = useState(null);
  const [acompteForm, setAcompteForm] = useState({
    montant: '',
    date: '',
    mode: 'virement',
    reference: '',
    isScheduled: false,
    scheduledDate: ''
  });

  // États pour les statistiques comptables
  const [_comptaStats, _setComptaStats] = useState({
    totalFacture: 0,
    totalEncaisse: 0,
    totalAcomptes: 0,
    totalEcheances: 0,
    sepaCore: { count: 0, montant: 0 },
    sepaB2B: { count: 0, montant: 0 },
    cb: { count: 0, montant: 0 },
    virement: { count: 0, montant: 0 }
  });

  // États pour les exports
  const [exportLoading, setExportLoading] = useState(false);

  // Chargement des données
  const fetchData = useCallback(async (showRefreshToast = false) => {
    if (showRefreshToast) setRefreshing(true);
    else setLoading(true);
    
    try {
      const [souscriptionsAdminRes, souscriptionsRCDRes, recusRes, coopersRes, membersRes, expertsRes] = await Promise.all([
        axios.get(`${API}/souscriptions/admin/all`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API}/souscriptions-rcd`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API}/recus-ess/liste?limit=100`, { headers: getAuthHeader() }).catch(() => ({ data: { recus: [] } })),
        axios.get(`${API}/coopers`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API}/members`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API}/experts`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      
      // Charger les listes pour l'assignation
      setCoopers(coopersRes.data?.coopers || coopersRes.data || []);
      setMembers(membersRes.data || []);
      setExperts(expertsRes.data?.experts || expertsRes.data || []);
      
      // Combiner toutes les souscriptions (contrats + avenants)
      // L'API admin/all retourne {souscriptions: [], total: N} donc on extrait le tableau
      const adminData = souscriptionsAdminRes.data;
      const allSouscriptions = Array.isArray(adminData) ? adminData : (adminData?.souscriptions || []);
      const rcdData = souscriptionsRCDRes.data;
      const souscriptionsRCD = Array.isArray(rcdData) ? rcdData : (rcdData?.souscriptions || []);
      
      // Créer un map pour dédupliquer par ID
      const documentsMap = new Map();
      
      // Ajouter les souscriptions admin
      allSouscriptions.forEach(doc => {
        documentsMap.set(doc.id, {
          ...doc,
          source: 'souscriptions',
          type_document: doc.type_document || (doc.numero_police?.startsWith('AV-') ? 'avenant' : 'contrat')
        });
      });
      
      // Ajouter/enrichir avec souscriptions RCD
      souscriptionsRCD.forEach(doc => {
        const existing = documentsMap.get(doc.id);
        if (existing) {
          documentsMap.set(doc.id, { ...existing, ...doc, source: 'both' });
        } else {
          documentsMap.set(doc.id, {
            ...doc,
            source: 'souscriptions_rcd',
            type_document: doc.type_document || (doc.numero_police?.startsWith('AV-') ? 'avenant' : 'contrat')
          });
        }
      });
      
      // Convertir en tableau et trier par date
      const allDocs = Array.from(documentsMap.values()).sort((a, b) => 
        new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0)
      );
      
      setAllDocuments(allDocs);
      setRecusESS(recusRes.data?.recus || []);
      
      if (showRefreshToast) {
        toast.success('Données actualisées');
      }
    } catch {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calcul du montant HT (Contribution Solidaire RCDS - correspond à "Contribution solidaire RCDS" du reçu ESS)
  const calculateMontantHT = useCallback((doc) => {
    // Priorité: montants_personnalises > tarification.montant_ht > montant_ht_contribution
    const tarif = doc.tarification || {};
    const montantsPerso = doc.montants_personnalises || tarif.montants_personnalises;
    
    if (montantsPerso?.montant_ht_personnalise && parseFloat(montantsPerso.montant_ht_personnalise) > 0) {
      return parseFloat(montantsPerso.montant_ht_personnalise);
    }
    
    if (tarif.montant_ht && tarif.montant_ht > 0) {
      return tarif.montant_ht;
    }
    
    // Champ tarification du simulateur BATICO
    if (tarif.montant_contribution_ht && tarif.montant_contribution_ht > 0) {
      return tarif.montant_contribution_ht;
    }
    
    if (doc.montant_ht_contribution && doc.montant_ht_contribution > 0) {
      return doc.montant_ht_contribution;
    }
    
    if (doc.montant_ht && doc.montant_ht > 0) {
      return doc.montant_ht;
    }
    
    return 0;
  }, []);

  // Calcul du Montant Global = HT + contributions ESS (même formule que le Devis ESS)
  // TVA non applicable – Structure ESS non assujettie (Art. 261 du CGI)
  const calculateMontantTTC = useCallback((doc) => {
    // 1. Priorité: montant_global calculé par le backend (source de vérité alignée avec Devis ESS)
    if (doc.montant_global && parseFloat(doc.montant_global) > 0) {
      return parseFloat(doc.montant_global);
    }
    
    const tarif = doc.tarification || {};
    const montantHT = calculateMontantHT(doc);
    
    if (montantHT > 0) {
      // 2. Vérifier taxes_personnalisees (top level ou dans tarification)
      const taxesPerso = doc.taxes_personnalisees || tarif.taxes_personnalisees;
      const taxesData = tarif.taxes || {};
      const taxesDetail = taxesPerso || taxesData.detail || [];
      
      if (taxesDetail.length > 0) {
        const totalTaxes = taxesDetail.reduce((sum, t) => {
          const montant = parseFloat(t.montant || 0);
          if (montant > 0) return sum + montant;
          const taux = parseFloat(t.taux || 0);
          return sum + (montantHT * taux / 100);
        }, 0);
        
        // Vérifier montant_ttc_personnalise
        const montantsPerso = doc.montants_personnalises || tarif.montants_personnalises;
        if (montantsPerso?.montant_ttc_personnalise && parseFloat(montantsPerso.montant_ttc_personnalise) > 0) {
          return parseFloat(montantsPerso.montant_ttc_personnalise);
        }
        return Math.round((montantHT + totalTaxes) * 100) / 100;
      }
      
      // Taux ESS par défaut: 4 × 9.50% = 38%
      return Math.round(montantHT * 1.38 * 100) / 100;
    }
    
    // Fallback sur les valeurs stockées
    if (tarif.montant_ttc && tarif.montant_ttc > 0) return tarif.montant_ttc;
    if (doc.montant_ttc_contribution && doc.montant_ttc_contribution > 0) return doc.montant_ttc_contribution;
    if (doc.montant_ttc && doc.montant_ttc > 0) return doc.montant_ttc;
    if (doc.montant_total && doc.montant_total > 0) return doc.montant_total;
    
    return 0;
  }, [calculateMontantHT]);

  // Documents en attente de validation
  const documentsEnAttente = useMemo(() => {
    return allDocuments.filter(doc => {
      const statut = doc.statut?.toLowerCase() || '';
      const paiementValide = doc.paiement_valide === true;
      
      return !paiementValide && (
        statut === 'en_attente' ||
        statut === 'en_attente_paiement' ||
        statut === 'en_attente_virement' ||
        statut === 'signee'
      );
    });
  }, [allDocuments]);

  // Documents avec paiement validé
  const documentsValides = useMemo(() => {
    return allDocuments.filter(doc => doc.paiement_valide === true);
  }, [allDocuments]);

  // Filtrage avancé
  const filteredDocuments = useMemo(() => {
    const sourceList = activeTab === 'attente' ? documentsEnAttente : documentsValides;
    
    return sourceList.filter(doc => {
      // Recherche textuelle
      const searchLower = searchTerm.toLowerCase().trim();
      // V36.90 — Normalisation des territoires (string CSV OU liste)
      const rawTerr = doc.territoires || doc.departements || doc.territoire || doc.departement;
      const territoiresList = Array.isArray(rawTerr)
        ? rawTerr.map(String)
        : (typeof rawTerr === 'string' ? rawTerr.split(',').map(t => t.trim()) : []);
      const matchSearch = !searchTerm ||
        (doc.raison_sociale || '').toLowerCase().includes(searchLower) ||
        (doc.numero_police || '').toLowerCase().includes(searchLower) ||
        (doc.siret || '').toLowerCase().includes(searchLower) ||
        (doc.reference || '').toLowerCase().includes(searchLower) ||
        (doc.id || '').toLowerCase().includes(searchLower) ||
        // V36.90 — Email + territoire
        (doc.user_email || doc.email || '').toLowerCase().includes(searchLower) ||
        territoiresList.some(t => t.toLowerCase().includes(searchLower));
      
      // Filtre par type
      const typeDoc = doc.type_document || (doc.numero_police?.startsWith('AV-') ? 'avenant' : 'contrat');
      const matchType = filterType === 'all' || typeDoc === filterType;
      
      // Filtre par montant
      const montantTTC = calculateMontantTTC(doc);
      const matchMontantMin = !filterMontantMin || montantTTC >= parseFloat(filterMontantMin);
      const matchMontantMax = !filterMontantMax || montantTTC <= parseFloat(filterMontantMax);
      
      // Filtre par date
      const docDate = doc.date_creation || doc.created_at;
      const matchDateDebut = !filterDateDebut || (docDate && new Date(docDate) >= new Date(filterDateDebut));
      const matchDateFin = !filterDateFin || (docDate && new Date(docDate) <= new Date(filterDateFin));
      
      // Filtre par statut de reçu
      let matchRecuStatus = true;
      if (filterRecuStatus !== 'all') {
        const hasPaiements = doc.paiements_partiels && doc.paiements_partiels.length > 0;
        const hasRecuEcheance = doc.paiements_partiels?.some(p => p.numero_recu || p.recu_genere);
        const isAcquitte = doc.est_solde === true || doc.paiement_valide === true;
        
        switch (filterRecuStatus) {
          case 'recu_echeance':
            matchRecuStatus = hasRecuEcheance;
            break;
          case 'recu_acquitte':
            matchRecuStatus = isAcquitte;
            break;
          case 'en_attente_recu':
            matchRecuStatus = hasPaiements && !hasRecuEcheance;
            break;
          case 'avec_paiements':
            matchRecuStatus = hasPaiements;
            break;
          default:
            matchRecuStatus = true;
        }
      }
      
      return matchSearch && matchType && matchMontantMin && matchMontantMax && matchDateDebut && matchDateFin && matchRecuStatus;
    });
  }, [activeTab, documentsEnAttente, documentsValides, searchTerm, filterType, filterMontantMin, filterMontantMax, filterDateDebut, filterDateFin, filterRecuStatus, calculateMontantTTC]);

  // Statistiques de base
  const stats = useMemo(() => {
    const enAttente = documentsEnAttente.length;
    const avenantEnAttente = documentsEnAttente.filter(d => d.type_document === 'avenant' || d.numero_police?.startsWith('AV-')).length;
    const valides = documentsValides.length;
    const montantEnAttente = documentsEnAttente.reduce((sum, d) => sum + calculateMontantTTC(d), 0);
    const montantValide = documentsValides.reduce((sum, d) => sum + calculateMontantTTC(d), 0);
    
    return { enAttente, avenantEnAttente, valides, montantEnAttente, montantValide };
  }, [documentsEnAttente, documentsValides, calculateMontantTTC]);

  // Statistiques avancées pour le tableau de bord
  const dashboardStats = useMemo(() => {
    // Documents avec paiements partiels
    const documentsAvecPaiementsPartiels = allDocuments.filter(d => 
      d.paiements_partiels && d.paiements_partiels.length > 0 && !d.est_solde
    );
    
    // Documents soldés (partiellement ou totalement payés)
    const documentsSoldes = allDocuments.filter(d => d.est_solde === true || d.paiement_valide === true);
    
    // Calcul des montants
    const totalDu = allDocuments.reduce((sum, d) => sum + calculateMontantTTC(d), 0);
    
    // Montant total payé (somme des paiements partiels + paiements validés)
    const totalPaye = allDocuments.reduce((sum, d) => {
      if (d.paiement_valide === true || d.est_solde === true) {
        return sum + calculateMontantTTC(d);
      }
      // Pour les documents avec paiements partiels non soldés
      if (d.paiements_partiels && d.paiements_partiels.length > 0) {
        return sum + (d.montant_paye || d.paiements_partiels.reduce((s, p) => s + (p.amount || p.montant || 0), 0));
      }
      return sum;
    }, 0);
    
    const resteAPayer = totalDu - totalPaye;
    
    // Échéanciers - documents dont le paiement est attendu dans les prochains jours
    const today = new Date();
    const _in7Days = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    const _in30Days = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
    
    // Documents en retard (plus de 30 jours depuis création sans paiement)
    const enRetard = documentsEnAttente.filter(d => {
      const dateCreation = new Date(d.date_creation || d.created_at);
      const daysSinceCreation = (today - dateCreation) / (1000 * 60 * 60 * 24);
      return daysSinceCreation > 30;
    });
    
    // Documents urgents (entre 15 et 30 jours)
    const urgents = documentsEnAttente.filter(d => {
      const dateCreation = new Date(d.date_creation || d.created_at);
      const daysSinceCreation = (today - dateCreation) / (1000 * 60 * 60 * 24);
      return daysSinceCreation > 15 && daysSinceCreation <= 30;
    });
    
    // Documents récents (moins de 15 jours)
    const recents = documentsEnAttente.filter(d => {
      const dateCreation = new Date(d.date_creation || d.created_at);
      const daysSinceCreation = (today - dateCreation) / (1000 * 60 * 60 * 24);
      return daysSinceCreation <= 15;
    });
    
    // Paiements partiels en cours
    const paiementsPartielsEnCours = documentsAvecPaiementsPartiels.length;
    const montantPartielRecu = documentsAvecPaiementsPartiels.reduce((sum, d) => {
      return sum + (d.montant_paye || d.paiements_partiels?.reduce((s, p) => s + (p.amount || p.montant || 0), 0) || 0);
    }, 0);
    const montantPartielRestant = documentsAvecPaiementsPartiels.reduce((sum, d) => {
      const total = calculateMontantTTC(d);
      const paye = d.montant_paye || d.paiements_partiels?.reduce((s, p) => s + (p.amount || p.montant || 0), 0) || 0;
      return sum + (total - paye);
    }, 0);
    
    // Taux de recouvrement
    const tauxRecouvrement = totalDu > 0 ? ((totalPaye / totalDu) * 100).toFixed(1) : 0;
    
    // Paiements et reçus archivés
    const paiementsArchives = [];
    allDocuments.forEach(doc => {
      if (doc.paiements_partiels) {
        doc.paiements_partiels.forEach(p => {
          if (p.archive) {
            paiementsArchives.push({
              ...p,
              souscription_id: doc.id,
              raison_sociale: doc.raison_sociale,
              numero_police: doc.numero_police
            });
          }
        });
      }
    });
    
    // Documents complètement soldés et archivés
    const documentsArchives = allDocuments.filter(d => 
      (d.est_solde === true || d.paiement_valide === true) && d.archive === true
    );
    
    // Souscriptions en attente sans email (bloquant SEPA)
    const sansEmail = documentsEnAttente.filter(d => !d.email && !d.membre_email);
    
    return {
      totalDocuments: allDocuments.length,
      documentsEnAttente: documentsEnAttente.length,
      documentsSoldes: documentsSoldes.length,
      paiementsPartielsEnCours,
      montantPartielRecu,
      montantPartielRestant,
      totalDu,
      totalPaye,
      resteAPayer,
      enRetard: enRetard.length,
      urgents: urgents.length,
      recents: recents.length,
      documentsEnRetard: enRetard,
      documentsUrgents: urgents,
      documentsRecents: recents,
      documentsAvecPaiementsPartiels,
      tauxRecouvrement,
      paiementsArchives,
      documentsArchives,
      totalArchives: paiementsArchives.length + documentsArchives.length,
      sansEmail: sansEmail.length,
      documentsSansEmail: sansEmail
    };
  }, [allDocuments, documentsEnAttente, calculateMontantTTC]);

  return {
    _acomptesData, _comptaStats, _recusESS, _selectedAcompte, _selectedPaiement, _setAcomptesData,
    _setComptaStats, _setShowAcomptesTab, _showAcomptesTab, _showRecuEcheanceModal, _showStripeCBModal, acompteForm,
    activeTab, allDocuments, archiving, assignForm, bulkProcessing, calculateMontantHT,
    calculateMontantTTC, coopers, creatingEcheancier, dashboardStats, documentsEnAttente, documentsValides,
    echeanceForm, echeancierForm, experts, exportLoading, fetchData, filterDateDebut,
    filterDateFin, filterMontantMax, filterMontantMin, filterRecuStatus, filterType, filteredDocuments,
    generatingRecu, getAuthHeader, loading, loadingPartialPayments, members, partialPaymentData,
    partialPaymentsHistory, processingPartialPayment, refreshing, searchTerm, selectedDocument, selectedIds,
    selectedPaiementIndex, sepaModalEmail, sepaModalName, setAcompteForm, setActiveTab, setAllDocuments,
    setArchiving, setAssignForm, setBulkProcessing, setCoopers, setCreatingEcheancier, setEcheanceForm,
    setEcheancierForm, setExperts, setExportLoading, setFilterDateDebut, setFilterDateFin, setFilterMontantMax,
    setFilterMontantMin, setFilterRecuStatus, setFilterType, setGeneratingRecu, setLoading, setLoadingPartialPayments,
    setMembers, setPartialPaymentData, setPartialPaymentsHistory, setProcessingPartialPayment, setRecusESS, setRefreshing,
    setSearchTerm, setSelectedAcompte, setSelectedDocument, setSelectedIds, setSelectedPaiement, setSelectedPaiementIndex,
    setSepaModalEmail, setSepaModalName, setShareForm, setSharing, setShowAdvancedFilters, setShowAssignModal,
    setShowEcheanceModal, setShowEcheancierModal, setShowEditAcompteModal, setShowPartialPaymentModal, setShowRecuEcheanceModal, setShowShareModal,
    setShowStripeCBModal, setShowStripeSepaModal, setShowValidationModal, setStripeProcessing, setStripeSepaType, setUpdatingEcheance,
    setValidating, setValidationData, shareForm, sharing, showAdvancedFilters, showAssignModal,
    showEcheanceModal, showEcheancierModal, showEditAcompteModal, showPartialPaymentModal, showShareModal, showStripeSepaModal,
    showValidationModal, stats, stripeProcessing, stripeSepaType, updatingEcheance, validating,
    validationData,
  };
}
